/**
 * What the two recurring sections share.
 *
 * They are one control each on three pages - Invoices, Tickets, and the customer page, which hosts
 * both - so they are builders taking a host rather than pages. `CustomerFilterId` narrows the list
 * and preselects the editor; `lockCustomer` hides the picker entirely, because the customer page is
 * already about one customer and offering to move a plan to another company from there is a trap.
 *
 * `onChanged` is the load-bearing part of the contract. Saving a template raises the invoices or
 * opens the tickets it already owed, so the page around it holds a list that has just gone stale -
 * and each host refreshes ONLY the lists a template can affect. Routing it through a full page
 * refresh would discard whatever editor is open elsewhere on that page, which is the mistake
 * `MonitorChanged` made on the Network page: a poll result snapping shut a credential panel
 * mid-typing.
 */
import type { StackPanel } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import { RecurrenceInterval, TicketPriority, type CustomerDto } from '../../contracts.gen.js';
import { due, intervalLabel, middayUtcFromIso, nextDue, todayIso } from '../../recurrence.js';
import type { PageContext } from '../page.js';

export interface RecurringSectionOptions {
  ctx: PageContext;
  /** Narrows the list to one customer and preselects the editor's picker. */
  customerFilterId?: number;
  /** Hides the customer picker. Set wherever the page is already about one customer. */
  lockCustomer?: boolean;
  /** Called after anything that can have raised invoices or opened tickets. */
  onChanged?: () => void;
}

export interface RecurringSection {
  readonly panel: StackPanel;
  /** Loads the pickers, then the list. Separate from `refresh` because the pickers load once. */
  initialize(): Promise<void>;
  refresh(): Promise<void>;
  /**
   * Re-scopes the section to one customer (or to all, with undefined). For a page whose own filter
   * narrows both halves of it - a combo is its own Apply button there, so the section has to follow.
   */
  setCustomerFilter(id: number | undefined): Promise<void>;
}

export const INTERVALS: readonly RecurrenceInterval[] = [
  RecurrenceInterval.Monthly,
  RecurrenceInterval.Quarterly,
  RecurrenceInterval.Yearly,
  RecurrenceInterval.Weekly
];

export const INTERVAL_NAMES: readonly string[] = INTERVALS.map(intervalLabel);

export const PRIORITIES: readonly TicketPriority[] = [
  TicketPriority.Low,
  TicketPriority.Normal,
  TicketPriority.High,
  TicketPriority.Urgent
];

export const PRIORITY_NAMES = ['Low', 'Normal', 'High', 'Urgent'] as const;

/** The customers a picker offers, and the ids behind them - the C# `_customerIds` parallel list. */
export interface CustomerChoices {
  names: string[];
  ids: number[];
}

/**
 * Loads the customer picker.
 *
 * A failure is swallowed rather than reported: the list of templates renders perfectly well without
 * a picker, and only the editor needs one. The same call the C# sections make.
 */
export async function loadCustomerChoices(signal?: AbortSignal): Promise<CustomerChoices> {
  try {
    const customers: CustomerDto[] = await crm.getCustomers(signal);
    return { names: customers.map(c => c.name), ids: customers.map(c => c.id) };
  } catch {
    return { names: [], ids: [] };
  }
}

/** What the schedule preview says when the day of the month is not a day of a month. */
export const BAD_DAY = 'Day of the month must be between 1 and 31.';

export interface ScheduleReading {
  /** Null when the day is out of range or the schedule can never fall due. */
  first: Date | null;
  /** How many occurrences saving would generate at once, because they are already owed. */
  owed: number;
  /** The message to show when `first` is null. */
  problem: string;
}

/**
 * Reads a schedule the way the server will.
 *
 * The preview IS the schedule, computed with the same `Recurrence` the generator uses - which is the
 * point of porting that file rather than approximating it here. The owed count matters because
 * saving a template whose start date is in the past raises those invoices or opens those tickets
 * immediately, and a form that did not say so would look like it had done nothing much.
 */
export function readSchedule(
  intervalIndex: number,
  dayText: string,
  startIso: string,
  endIso: string
): ScheduleReading {
  const day = Number.parseInt(dayText, 10);
  if (!Number.isFinite(day) || day < 1 || day > 31) {
    return { first: null, owed: 0, problem: BAD_DAY };
  }

  const interval = INTERVALS[Math.max(0, Math.min(INTERVALS.length - 1, intervalIndex))]!;
  const start = middayUtcFromIso(startIso) ?? middayUtcFromIso(todayIso())!;
  const end = middayUtcFromIso(endIso);
  const first = nextDue(interval, day, start, end, null, 0, new Set<string>());
  if (!first) {
    return { first: null, owed: 0, problem: 'This schedule never falls due - check the start and end dates.' };
  }
  return { first, owed: due(interval, day, start, end, null, new Date()).length, problem: '' };
}

/** The clause every preview ends with when an end date is set. */
export function endsClause(endIso: string): string {
  const end = middayUtcFromIso(endIso);
  return end ? ` Ends ${endIso.trim()}.` : '';
}

/** Turns a thrown call into a status message, with a prefix naming what was being done. */
export function failure(prefix: string, error: unknown): string {
  const detail = describeError(error);
  return detail ? `${prefix}: ${detail}` : prefix;
}
